import React, { useRef, useEffect, useState } from 'react';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  children: React.ReactNode;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, onClose, children }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Use a completely different approach - position menu directly at click coordinates
  // and use CSS transform to keep it in viewport if needed
  const [menuSize, setMenuSize] = useState({ width: 0, height: 0 });
  
  // Measure menu size after render and handle clicks outside
  useEffect(() => {
    if (!menuRef.current) return;
    
    const rect = menuRef.current.getBoundingClientRect();
    setMenuSize({
      width: rect.width,
      height: rect.height
    });
    
    // Close menu when clicking outside
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    // Use mousedown with capture phase for better responsiveness
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [onClose]);
  
  // Calculate transform to keep menu in viewport
  const getTransform = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let translateX = 0;
    let translateY = 0;
    
    // Check if menu extends beyond right edge
    if (x + menuSize.width > viewportWidth) {
      translateX = -menuSize.width;
    }
    
    // Check if menu extends beyond bottom edge
    if (y + menuSize.height > viewportHeight) {
      translateY = -menuSize.height;
    }
    
    return { translateX, translateY };
  };
  
  const { translateX, translateY } = getTransform();
  
  return (
    <div 
      ref={menuRef}
      className="fixed backdrop-blur-md bg-white/90 dark:bg-gray-800/90 shadow-xl rounded-md z-50 min-w-[150px] border border-gray-200/50 dark:border-gray-600/50"
      style={{ 
        left: x, 
        top: y,
        transform: `translate(${translateX}px, ${translateY}px)`,
        transformOrigin: 'top left'
      }}
      data-context-menu-position={`x:${x},y:${y}`}
    >
      {children}
    </div>
  );
};

interface ContextMenuItemProps {
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  /** Lens SP1 — defined = checklist row: ✓-gutter, no hover-scale bounce. */
  checked?: boolean;
}

export const ContextMenuItem: React.FC<ContextMenuItemProps> = ({
  icon, label, onClick, disabled = false, danger = false, checked
}) => {
  const isChecklist = checked !== undefined;
  return (
    <div
      className={`
        px-4 py-2 flex items-center cursor-pointer transition-all duration-200 backdrop-blur-sm
        hover:bg-gray-100/60 dark:hover:bg-gray-700/60 hover:backdrop-blur-md
        ${disabled ? 'opacity-50 cursor-not-allowed' : isChecklist ? '' : 'hover:scale-[1.02]'}
        ${danger ? 'text-red-600 dark:text-red-400 hover:bg-red-50/60 dark:hover:bg-red-900/30' : ''}
      `}
      onClick={disabled ? undefined : onClick}
    >
      {isChecklist && (
        <span data-testid="menu-check-gutter" className="w-4 inline-block shrink-0">
          {checked ? '✓' : ''}
        </span>
      )}
      {icon && <span className="mr-2">{icon}</span>}
      <span>{label}</span>
    </div>
  );
};
